import { Component, Input, OnInit } from '@angular/core';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { LlenardatosComponent } from '../llenardatos/llenardatos.component';
import { Metodosui } from '../../services/metodosui';
import { FormModel } from '../../models/dynamic-form';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-presupuestosedit',
  templateUrl: './presupuestosedit.component.html',
  styleUrls: ['./presupuestosedit.component.scss'],
  standalone: false
})
export class PresupuestoseditComponent implements OnInit {

  @Input() trabajo: any;
  @Input() mode: string = 'add';

  @Input() trabajoItemId: string | null = null;
  @Input() trabajoItems: any[] = []; // Job items from parent
  items: any[] = [];
  headerData: any = { estado: '', dias: '', notas: '', fecha: '' };
  loading = false;

  constructor(
    private modalCtrl: ModalController,
    private actionSheetCtrl: ActionSheetController,
    private metodosUi: Metodosui,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService
  ) { }

  ngOnInit() {
    if (this.mode === 'list') {
      this.loadHeader(); // Load Header
      this.loadItems();
      this.formatJobItems();
    }
  }

  jobItemsText: string = "";

  formatJobItems() {
    if (this.trabajoItems && this.trabajoItems.length > 0) {
      this.jobItemsText = this.trabajoItems.map(i => i.descripcion).join(', ');
    } else {
      this.jobItemsText = "Sin items descriptivos";
    }
  }

  close() {
    this.modalCtrl.dismiss();
  }

  async loadItems() {
    this.loading = true;
    try {
      const data: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            "genModel", "presupuestoItems", String(this.trabajo.id), '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      if (Array.isArray(data)) {
        let mappedItems = data.map(i => {
          const type = i.dt10;
          let item: any = {
            id: i.dt1,
            tipo: type,
            actividad: i.dt2, // Default title
            total: Number(i.dt7),
            nota: '',
            icon: 'help-circle-outline',
            trabajo_item_id: i.dt9, // Mapped from SQL
            raw1: i.dt11, // Actividad/Material/Subcontratista/Medida ID
            raw2: i.dt12,  // Rol ID
            activo: i.dt13 // Active status
          };

          if (type === 'MOBRA') {
            item.rolVal = item.raw2; // Rol ID
            item.actividadVal = item.raw1; // Actividad ID
            item.rol = i.dt3;
            item.cantidad = i.dt4;
            item.dias = i.dt5;
            item.salario = i.dt6;
            item.nota = i.dt8;
            item.icon = 'construct-outline';
            // item.activo mapped above
          } else if (type === 'MATERIAL') {
            item.cantidad = i.dt4;
            item.precio = i.dt5;
            item.icon = 'cube-outline';
            item.rol = i.dt3; // Measure Unit Name
            item.rolVal = item.raw1; // Measure Unit ID
            item.nota = i.dt8;
            item.materialVal = i.dt2; // Material Name (Text)
          } else if (type === 'GASTO') {
            item.precio = i.dt5;
            item.icon = 'cash-outline';
            item.rol = 'Gasto Vario';
            item.conceptoVal = i.dt2;
          } else if (type === 'SUB') {
            item.nota = i.dt3; // Detalle
            item.precio = i.dt5;
            item.icon = 'business-outline';
            item.rol = 'Subcontrato';
            item.subVal = item.raw1; // Subcontratista ID
          } else if (type === 'MEDIDA') {
            item.rol = i.dt3; // Unidad (Text)
            item.cantidad = i.dt4;
            item.precio = i.dt5;
            item.nota = i.dt8; // Color
            item.icon = 'resize-outline';
            item.rolVal = i.dt11 || i.dt2; // Unit ID (from dt11 fix)
            item.unidadVal = i.dt11; // Unit ID
            item.modoVal = i.dt2; // Description
          }

          return item;
        });

        // Generate summary for General View (uses all items)
        this.generateSummary(mappedItems);

        // Filter by trabajoItemId if present (Item View)
        if (this.trabajoItemId) {
          this.items = mappedItems.filter(i => i.trabajo_item_id == this.trabajoItemId);
        } else {
          this.items = mappedItems;
        }
      }
    } catch (e) {
      console.error('Error loading items:', e);
    } finally {
      this.loading = false;
    }
  }

  // New properties for General View
  totalBudget: number = 0;
  summaryText: string = '';

  generateSummary(allItems: any[]) {
    this.totalBudget = allItems.reduce((acc, i) => acc + (Number(i.total) || 0), 0);

    // Group by type for summary text
    const summary = [];
    const mobra = allItems.filter(i => i.tipo === 'MOBRA').length;
    const mat = allItems.filter(i => i.tipo === 'MATERIAL').length;
    const other = allItems.length - mobra - mat;

    if (mobra > 0) summary.push(`${mobra} Items de Mano de Obra`);
    if (mat > 0) summary.push(`${mat} Materiales`);
    if (other > 0) summary.push(`${other} Otros Items`);

    this.summaryText = summary.join(', ') || 'Sin items registrados';
  }

  // Helper for JSON mapping
  getJsonForType(type: string): string {
    switch (type) {
      case 'MOBRA': return 'dy_m_presupuesto_mobra';
      case 'MATERIAL': return 'dy_m_presupuesto_materiales';
      case 'GASTO': return 'dy_m_presupuesto_gastos';
      case 'MEDIDA': return 'dy_m_presupuesto_medidas';
      case 'SUB': return 'dy_m_presupuesto_subcontratos';
    }
    return '';
  }

  async presentAddOptions() {
    const buttons = [
      { text: 'Mano de Obra', icon: 'construct-outline', code: 'MANO_OBRA', json: 'dy_m_presupuesto_mobra' },
      { text: 'Materiales', icon: 'cube-outline', code: 'MATERIAL', json: 'dy_m_presupuesto_materiales' },
      { text: 'Gastos Varios', icon: 'cash-outline', code: 'GASTO', json: 'dy_m_presupuesto_gastos' },
      { text: 'Medidas', icon: 'resize-outline', code: 'MEDIDA', json: 'dy_m_presupuesto_medidas' },
      { text: 'Subcontratos', icon: 'business-outline', code: 'SUB', json: 'dy_m_presupuesto_subcontratos' }
    ];

    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Agregar Item de Presupuesto',
      buttons: [
        ...buttons.map(b => ({
          text: b.text,
          icon: b.icon,
          handler: () => this.openItemForm(b.code, b.json)
        })),
        {
          text: 'Cancelar',
          icon: 'close',
          role: 'cancel'
        }
      ]
    });
    await actionSheet.present();
  }

  async toggleActive(item: any, event: any) {
    if (event) event.stopPropagation();

    // Determine new status (S/N)
    const isChecked = event.detail.checked;
    const newStatus = isChecked ? 'S' : 'N';
    const oldStatus = item.activo;

    // Optimistic Update
    item.activo = newStatus;

    const normalizedType = item.tipo === 'MOBRA' ? 'MANO_OBRA' : item.tipo;


    try {
      const payload = this.metodosGenService.setModeloGeneral(
        normalizedType,
        'TOGGLE_ACTIVE',
        String(item.id),
        String(this.trabajo.id),
        '', '', '', '', '', '', '', // p1..p7 empty
        newStatus, // p8 (dt10)
        String(item.trabajo_item_id), // dt11 (trabajo_item_id)
        '0', // p9 (dt12)
        '', '', ''
      );

      const res = await firstValueFrom(this.javapiService.savePresupuesto(payload));

      if (!res || !res.success) {
        throw new Error('API returned failure');
      }
      // Success
    } catch (error) {
      console.error('Error toggling active status:', error);
      item.activo = oldStatus; // Revert
      // Force toggle back visually if needed (Angular binding handles it if item.activo changes back)
      this.metodosUi.showToast('Error al actualizar estado.', 'danger');
    }
  }

  isActive(item: any): boolean {
    if (item.activo === undefined || item.activo === null) return true; // Default to true if not set
    return item.activo === 'S' || item.activo == 1 || item.activo === '1' || item.activo === true;
  }

  async editItem(item: any) {
    const jsonParam = this.getJsonForType(item.tipo);
    await this.openItemForm(item.tipo, jsonParam, item);
  }

  async openItemForm(code: string, jsonParam: string, existingItem: any = null) {
    try {
      const jsonStr = await firstValueFrom(
        this.javapiService.getCommonStr(
          this.metodosGenService.setModeloGeneral(
            "genParStr", "parm4", jsonParam, '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );

      if (!jsonStr) {
        this.metodosUi.showToast('Error al cargar configuración', 'danger');
        return;
      }

      const formConfig = JSON.parse(jsonStr);
      let formComps = formConfig.components || formConfig;

      // Pre-fill values if editing
      if (existingItem) {
        const getComp = (num: string) => {
          // Find inside array or object
          if (Array.isArray(formComps)) return formComps.find((c: any) => c.ngmNumber == num);
          for (let key in formComps) {
            if (Array.isArray(formComps[key])) {
              const found = formComps[key].find((c: any) => c.ngmNumber == num);
              if (found) return found;
            }
          }
          return null;
        };

        const getActivoComp = () => {
          let c = getComp('9');
          if (!c) {
            const search = (comps: any[]) => comps.find((x: any) => x.nombre?.toLowerCase().includes('activo') || x.titulo?.toLowerCase().includes('activo') || x.label?.toLowerCase().includes('activo'));
            if (Array.isArray(formComps)) c = search(formComps);
            else {
              for (let k in formComps) {
                if (Array.isArray(formComps[k])) {
                  c = search(formComps[k]);
                  if (c) break;
                }
              }
            }
          }

          else console.warn('Activo Comp NOT FOUND');
          return c;
        };

        switch (code) {
          case 'MOBRA':
            // 1=Act, 2=Rol, 3=Cant, 4=Dias, 5=Salario, 6=Seguro, 7=Transporte, 8=Nota, 9=Activo
            let c1 = getComp('1'); if (c1) c1.value = { dt1: existingItem.actividadVal, dt2: existingItem.actividad }; // Actividad
            let c2 = getComp('2'); if (c2) c2.value = { dt1: existingItem.rolVal, dt2: existingItem.rol }; // Rol
            let c3 = getComp('3'); if (c3) c3.value = existingItem.cantidad;
            let c4 = getComp('4'); if (c4) c4.value = existingItem.dias;
            let c5 = getComp('5'); if (c5) c5.value = existingItem.salario;
            let c8 = getComp('8'); if (c8) c8.value = existingItem.nota;
            // Active handling
            let c9 = getActivoComp(); if (c9) c9.value = (existingItem.activo === 'S' || existingItem.activo == 1 || existingItem.activo === '1' || existingItem.activo === true);
            break;
          case 'MATERIAL':
            // 1=Mat, 2=Cant, 3=Medida, 4=Precio, 5=Nota, 9=Activo
            let m1 = getComp('1'); if (m1) m1.value = existingItem.materialVal;
            let m2 = getComp('2'); if (m2) m2.value = existingItem.cantidad;
            let m3 = getComp('3'); if (m3) m3.value = { dt1: existingItem.rolVal, dt2: existingItem.rol }; // Medida {id, name}
            let m4 = getComp('4'); if (m4) m4.value = existingItem.precio;
            let m5 = getComp('5'); if (m5) m5.value = existingItem.nota;
            let m9 = getActivoComp(); if (m9) m9.value = (existingItem.activo === 'S' || existingItem.activo == 1 || existingItem.activo === '1' || existingItem.activo === true);
            break;
          case 'GASTO':
            let g1 = getComp('1'); if (g1) g1.value = existingItem.conceptoVal;
            let g2 = getComp('2'); if (g2) g2.value = existingItem.precio;
            let g9 = getActivoComp(); if (g9) g9.value = (existingItem.activo === 'S' || existingItem.activo == 1 || existingItem.activo === '1' || existingItem.activo === true);
            break;
          case 'SUB':
            let s1 = getComp('1'); if (s1) s1.value = { dt1: existingItem.subVal, dt2: existingItem.actividad }; // Subcontratista {id, name}
            let s2 = getComp('2'); if (s2) s2.value = existingItem.nota;
            let s3 = getComp('3'); if (s3) s3.value = existingItem.precio;
            let s9 = getActivoComp(); if (s9) s9.value = (existingItem.activo === 'S' || existingItem.activo == 1 || existingItem.activo === '1' || existingItem.activo === true);
            break;
          case 'MEDIDA':
            let d1 = getComp('1'); if (d1) d1.value = existingItem.modoVal;
            let d2 = getComp('2'); if (d2) d2.value = { dt1: existingItem.unidadVal, dt2: existingItem.rol }; // Unit {id, name}
            let d3 = getComp('3'); if (d3) d3.value = existingItem.cantidad;
            let d4 = getComp('4'); if (d4) d4.value = existingItem.precio;
            let d5 = getComp('5'); if (d5) d5.value = existingItem.nota;
            let d9 = getActivoComp(); if (d9) d9.value = (existingItem.activo === 'S' || existingItem.activo == 1 || existingItem.activo === '1' || existingItem.activo === true);

            // Load Details for Calculator
            const loadCalcData = async () => {
              const details = await this.loadMeasureDetails(String(existingItem.id));

              const d10 = getComp('10') || (formConfig.components.buttonmodal && formConfig.components.buttonmodal.find((c: any) => c.modal === 'MeasureCalculatorComponent'));
              if (d10) {
                d10.value = {
                  totalArea: existingItem.cantidad,
                  unit: existingItem.rol,
                  details: details
                };
              }
            };
            loadCalcData();
            break;
        }

        // Add Delete Button if editing
        if (!formConfig.components.button.find((b: any) => b.action === 'delete')) {
          formConfig.components.button.unshift({
            nombre: "Eliminar",
            action: "delete",
            color: "danger",
            isquestion: true,
            question: "¿Eliminar este item?"
          });
        }
      }

      const model: FormModel = {
        mostrar: { titulo: existingItem ? 'Editar Item' : 'Agregar Item', color: 'primary' },
        datos: {},
        components: formConfig.components || formConfig
      };

      const savedData = await this.metodosUi.openModal(LlenardatosComponent, { model });
      if (savedData) {

        const action = savedData.action; // 'save' or 'delete'
        if (action === 'close' || action === 'cancel') return;

        // Extract Data
        const modelData = savedData.data || savedData;
        const comps = modelData.components || {};
        const allComps = [
          ...(comps.input || []), ...(comps.selectOption || []), ...(comps.textarea || []),
          ...(comps.toggle || []), ...(comps.date || []), ...(comps.takePic || []), ...(comps.buttonmodal || [])
        ];

        const getVal = (num: string) => {
          const found = allComps.find((c: any) => c.ngmNumber == num);
          if (!found) return '';
          if (found.value && typeof found.value === 'object') return found.value.dt1 || found.value.id || found.value;
          return found.value;
        };

        let p1 = '', p2 = '', p3 = '', p4 = '', p5 = '', p6 = '', p7 = '', p8 = '', p9 = '';

        switch (code) {
          case 'MANO_OBRA':
          case 'MOBRA': // Handle alias
            code = 'MANO_OBRA';
            p1 = getVal('1'); // Actividad
            p2 = getVal('2'); // Rol
            p3 = getVal('3'); // Cantidad
            p4 = getVal('4'); // Dias
            p5 = getVal('5'); // Salario
            p6 = getVal('6'); // Seguro
            p7 = getVal('8'); // Nota
            p8 = getVal('9') ? 'S' : 'N'; // Activo
            p9 = getVal('7'); // Transporte
            break;
          case 'MATERIAL':
            p1 = getVal('1'); // Material
            p3 = getVal('2'); // Cantidad
            p2 = getVal('3'); // Medida ID
            p5 = getVal('4'); // Precio Unitario
            p7 = getVal('5'); // Detalle/Nota
            p8 = getVal('9') ? 'S' : 'N'; // Activo
            break;
          case 'GASTO':
            p1 = getVal('1'); // Concepto
            p5 = getVal('2'); // Monto
            p8 = getVal('9') ? 'S' : 'N'; // Activo
            break;
          case 'SUB':
            p1 = getVal('1'); // Subcontratista ID
            p7 = getVal('2'); // Detalle
            p5 = getVal('3'); // Costo
            p8 = getVal('9') ? 'S' : 'N'; // Activo
            break;
          case 'MEDIDA':
            p1 = getVal('1'); // Modo
            p2 = getVal('2'); // Unidad
            p3 = getVal('3'); // Cantidad
            p5 = getVal('4'); // Precio
            p7 = getVal('5'); // Color
            p8 = getVal('9') ? 'S' : 'N'; // Activo
            break;
        }

        const sqlAction = action === 'delete' ? 'DELETE' : (existingItem ? 'UPDATE' : 'INSERT');
        const targetId = existingItem ? String(existingItem.id) : "";

        const payload = this.metodosGenService.setModeloGeneral(
          code, sqlAction,
          targetId, String(this.trabajo.id),
          p1 || "", p2 || "", p3 || "", p4 || "", p5 || "", p6 || "", p7 || "", p8 || "",
          this.trabajoItemId || "", // dt11 = trabajo_item_id
          p9 || "", "", "", "" // dt12=p9
        );

        const res = await firstValueFrom(this.javapiService.savePresupuesto(payload));
        if (res && res.success) {

          // Handle Measures Details Saving
          // ONLY if not deleting. If deleting, the parent is gone, so details are gone (cascade) or don't matter.
          if (code === 'MEDIDA' && sqlAction !== 'DELETE') {
            if (comps.buttonmodal) {
            } else {
              console.warn('DEBUG: comps.buttonmodal is MISSING or empty');
            }

            // Find calculator component dynamically by its modal class name
            const calculatorComp = allComps.find((c: any) => c.modal === 'MeasureCalculatorComponent');
            const measureDetailsObj = calculatorComp ? (calculatorComp.value && (calculatorComp.value.dt1 || calculatorComp.value.id || calculatorComp.value)) : null;


            // If we have a returned object with details array
            if (measureDetailsObj && measureDetailsObj.details && Array.isArray(measureDetailsObj.details)) {
              const newId = res.id || targetId; // Use returned ID for INSERT, or existing for UPDATE
              if (newId) {
                // 1. Delete old details
                const delPayload = this.metodosGenService.setModeloGeneral(
                  "MEDIDA", "DELETE_ALL_BY_PARENT",
                  "", String(newId), // dt2 = medida_id
                  "", "", "", "", "", "", "", "", "", "", "", "", ""
                );
                // Reuse savePresupuestoDetalle endpoint for this custom action
                try {
                  await firstValueFrom(this.javapiService.savePresupuestoDetalle(delPayload));

                  // 2. Insert new details
                  const details: any[] = measureDetailsObj.details;
                  const promises = details.map(d => {
                    const detailPayload = this.metodosGenService.setModeloGeneral(
                      "MEDIDA", "INSERT",
                      "0", String(newId), // dt1=0 (new), dt2=medida_id
                      d.descripcion || "", // dt3
                      String(d.ancho || 0), // dt4
                      String(d.alto || 0), // dt5
                      d.unidad || "m", // dt6
                      String(d.area || 0), // dt7
                      d.foto || "", // dt8
                      "", "", "", "", "", d.originalFoto || "", JSON.stringify(d.annotations || []) // dt9..dt13 empty, dt14=originalFoto, dt15=annotations
                    );
                    return firstValueFrom(this.javapiService.savePresupuestoDetalle(detailPayload));
                  });

                  await Promise.all(promises);

                } catch (err) {
                  console.error('Error saving measure details', err);
                  this.metodosUi.showToast('Error al guardar detalles de medida', 'warning');
                }
              }
            }
          }

          this.metodosUi.showToast(sqlAction === 'DELETE' ? 'Eliminado' : 'Guardado', 'success');
          this.loadItems();
        } else {
          this.metodosUi.showToast('Error al procesar solicitud', 'danger');
        }
      }
    } catch (e) {
      console.error('Error in form:', e);
      this.metodosUi.showToast('Error', 'danger');
    }
  }

  async loadHeader() {
    try {
      const data: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            "genModel", "presupuestoHeader", String(this.trabajo.id), '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      if (Array.isArray(data) && data.length > 0) {
        const h = data[0];
        this.headerData = {
          id: h.dt1,
          estado: h.dt2,
          fecha: h.dt3,
          dias: h.dt4,
          notas: h.dt5
        };
      }
    } catch (e) {
      console.error('Error loading header:', e);
    }
  }

  async updateHeader() {
    const payload = this.metodosGenService.setModeloGeneral(
      "HEADER", "UPDATE",
      "", String(this.trabajo.id),
      String(this.headerData.estado), // dt3 = p_p1
      String(this.headerData.dias),   // dt4 = p_p2
      "", "", "", "",
      String(this.headerData.notas),  // dt9 = p_p7
      "", "", "", "", "", ""
    );

    try {
      const res = await firstValueFrom(this.javapiService.savePresupuesto(payload));
      if (res && res.success) {
        this.metodosUi.showToast('Cabecera actualizada', 'success');
      } else {
        this.metodosUi.showToast('Error al actualizar cabecera', 'danger');
      }
    } catch (e) {
      this.metodosUi.showToast('Error de conexión', 'danger');
    }
  }

  async deleteBudget() {
    const confirm = await this.metodosUi.showConfirmation("Eliminar Presupuesto", "¿Estás seguro? Se eliminarán todos los items.");
    if (!confirm) return;

    const payload = this.metodosGenService.setModeloGeneral(
      "HEADER", "DELETE",
      "", String(this.trabajo.id),
      "", "", "", "", "", "", "", "", "", "", "", "", ""
    );

    try {
      const res = await firstValueFrom(this.javapiService.savePresupuesto(payload));
      if (res && res.success) {
        this.metodosUi.showToast('Presupuesto eliminado', 'success');
        this.modalCtrl.dismiss(); // Close specific to generic usage? Or pass data?
      } else {
        this.metodosUi.showToast('Error al eliminar', 'danger');
      }
    } catch (e) {
      this.metodosUi.showToast('Error de conexión', 'danger');
    }
  }

  async loadMeasureDetails(medidaId: string): Promise<any[]> {
    try {
      if (!medidaId) return [];
      const data: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            "selDtsList", "measureDetails", medidaId, '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      if (Array.isArray(data)) {
        const details = data.map(i => ({
          id: i.dt1,
          descripcion: i.dt2,
          ancho: Number(i.dt3 || 0),
          alto: Number(i.dt4 || 0),
          unidad: i.dt5,
          area: Number(i.dt6 || 0),
          foto: i.dt7,
          originalFoto: null as string | null,
          nota: i.dt8,
          annotations: []
        }));

        // Fetch Base64 for images and Metadata for annotations
        for (let d of details) {
          if (d.foto && d.foto.length > 5 && d.foto !== 'null') { // check for valid filename
            const filename = d.foto;
            try {
              // Parallel fetch: Image + Metadata + Original(Clean) Image
              const origFilename = filename + "_orig";
              const [base64, metadata, base64Orig] = await Promise.all([
                firstValueFrom(this.javapiService.getImage(filename)).catch(() => null),
                firstValueFrom(this.javapiService.getMedidaMetadata(filename)).catch(() => null),
                firstValueFrom(this.javapiService.getImage(origFilename)).catch(() => null)
              ]);

              if (base64) {
                d.foto = base64; // Replace filename with Base64 content
                // Use Clean Original if available, otherwise fallback to Burned-in Image
                d.originalFoto = base64Orig ? base64Orig : base64;
              }

              if (metadata && typeof metadata === 'string' && metadata.trim().startsWith('[')) {
                try {
                  d.annotations = JSON.parse(metadata);
                } catch (e) { console.warn('Invalid metadata JSON', e); }
              }

            } catch (err) {
              console.warn('Failed to load assets for detail:', d.id);
            }
          } else {
            d.foto = null;
          }
        }
        return details;
      }
      return [];
    } catch (e) {
      console.error('Error loading details:', e);
      return [];
    }
  }

}
